import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { imageApi } from '@/lib/api';
import { ImageRecord } from '@/types';
import { RefreshCw } from 'lucide-react';
import { ImageUpload } from '@/components/ui/image-upload';

interface ImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingImage?: ImageRecord | null;
  workflows: string[];
  onSuccess: () => void;
}

export default function ImageUploadDialog({
  open,
  onOpenChange,
  editingImage,
  workflows,
  onSuccess,
}: ImageUploadDialogProps) {
  const [workflowName, setWorkflowName] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [error, setError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const workflowInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!editingImage;

  // 生成14位随机accessKey（与后端逻辑一致）
  const generateAccessKey = () => {
    const chars = 'abcdefghjkmnpqrstuvwxyz23456789'; // 去除 i, l, o, 0, 1
    let result = '';
    
    for (let i = 0; i < 14; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    setAccessKey(result);
  };

  // 服务端换图闸门的错误码兜底翻译。前端预检按文件名后缀判断，
  // 用户若把 jpg 改名成 .png 上传则预检放行、由服务端按真实字节格式拒绝，会走到这里。
  const translateUpdateError = (result?: { error?: string; code?: string }): string => {
    if (!result) return '';
    if (result.code === 'IMAGE_FORMAT_MISMATCH') {
      return '所选图片的实际格式与原图不一致（文件后缀可能与真实内容不符）。' +
        '换图必须保持格式不变，否则会破坏已有的永久链接引用。';
    }
    if (result.code === 'UNSUPPORTED_IMAGE_FORMAT') {
      return '无法识别该文件的图片格式，请上传 png / jpg / webp / gif 格式的图片。';
    }
    return result.error || '更新失败';
  };

  // 更新图片记录的mutation（编辑模式）
  const updateImageMutation = useMutation({
    mutationFn: async (data: { id: string; workflowName: string; image?: File }) => {
      return await imageApi.updateImage(data.id, {
        workflowName: data.workflowName,
        image: data.image
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
        onOpenChange(false);
        resetForm();
      } else {
        setError(translateUpdateError(result));
      }
    },
    onError: (error: any) => {
      setError(
        translateUpdateError(error?.response?.data) ||
        error.message || '操作失败'
      );
    },
  });

  // 初始化表单数据
  useEffect(() => {
    if (open) {
      if (isEditing && editingImage) {
        setWorkflowName(editingImage.workflowName);
        setAccessKey(''); // 编辑模式不显示accessKey
        setSelectedFile(null); // 清空选择的文件
        setPreviewUrl(''); // 清空预览
      } else {
        // 新建模式：重置表单并生成accessKey
        setWorkflowName('');
        setAccessKey('');
        setSelectedFile(null);
        setPreviewUrl('');
        generateAccessKey(); // 自动生成accessKey
      }
      setError('');
      
      // 设置焦点到工作流名称输入框
      setTimeout(() => {
        workflowInputRef.current?.focus();
      }, 100); // 延迟确保Dialog完全渲染后再设置焦点
    }
  }, [open, isEditing, editingImage]);

  // 重置表单（简化版本，用于其他地方）
  const resetForm = () => {
    setWorkflowName('');
    setAccessKey('');
    setError('');
    setSelectedFile(null);
    setPreviewUrl('');
  };

  // 创建新图片记录的mutation
  const createImageMutation = useMutation({
    mutationFn: async (data: { workflowName: string; image: File; accessKey?: string }) => {
      return await imageApi.createImage(data);
    },
    onSuccess: (result) => {
      if (result.success) {
        onSuccess();
        onOpenChange(false);
        resetForm();
      } else {
        setError(result.error || '创建失败');
      }
    },
    onError: (error: any) => {
      setError(error?.response?.data?.error || error.message || '操作失败');
    },
  });

  // 换图格式闸门（前端预检）：链接后缀是永久契约，换图不得改格式。
  // 服务端才是权威（按真实字节格式判定，挡得住改名伪装），这里只是让用户在点提交前就知道，
  // 不必等一次完整上传往返。.jpg/.jpeg 视为同格式，与后端 normalizeImageExt 保持一致。
  const normalizeExt = (ext: string) => {
    const e = ext.toLowerCase();
    return e === '.jpeg' ? '.jpg' : e;
  };

  const checkFormatAgainstExisting = (file: File): string | null => {
    if (!isEditing || !editingImage) return null;
    const existingExt = normalizeExt(editingImage.filename.slice(editingImage.filename.lastIndexOf('.')));
    const incomingExt = normalizeExt(file.name.slice(file.name.lastIndexOf('.')));
    if (!incomingExt || !existingExt || incomingExt === existingExt) return null;
    return `换图必须与原图同格式：该图片的永久链接以 ${existingExt} 结尾，` +
      `外部系统会按这个后缀识别格式，改成 ${incomingExt} 会导致已有引用解析出错。` +
      `请上传 ${existingExt} 格式的图片；确需换格式请新建一条记录（会得到新链接）。`;
  };

  // 处理文件选择（来自ImageUpload组件）
  const handleFileChange = (file: File | null) => {
    if (file) {
      setSelectedFile(file);
      setError(checkFormatAgainstExisting(file) || '');

      // 生成预览
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // 取消选择
      setSelectedFile(null);
      setPreviewUrl('');
    }
  };


  // 表单提交
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!workflowName.trim()) {
      setError('请输入归属工作流名称');
      return;
    }

    // 验证accessKey（仅在新建模式）
    if (!isEditing) {
      if (!accessKey.trim()) {
        setError('请输入图片ID');
        return;
      }
      
      if (!/^[a-hjkmnp-z2-9]{14}$/.test(accessKey.trim())) {
        setError('图片ID必须是14位字符，只能包含字母（除i,l,o）和数字（除0,1）');
        return;
      }
    }

    if (isEditing && editingImage) {
      // 提交前再拦一道（用户可能忽略选文件时的提示直接提交）
      if (selectedFile) {
        const formatError = checkFormatAgainstExisting(selectedFile);
        if (formatError) {
          setError(formatError);
          return;
        }
      }

      // 编辑模式：更新工作流名称和/或图片
      updateImageMutation.mutate({
        id: editingImage.id,
        workflowName: workflowName.trim(),
        image: selectedFile || undefined, // 如果选择了新图片则传入
      });
    } else {
      // 新建模式：需要选择文件
      if (!selectedFile) {
        setError('请选择要上传的图片');
        return;
      }

      createImageMutation.mutate({
        workflowName: workflowName.trim(),
        image: selectedFile,
        accessKey: accessKey.trim(),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? '编辑图片记录' : '添加图片'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 图片ID - 只在新建模式显示 */}
          {!isEditing && (
            <div className="space-y-2">
              <Label htmlFor="accessKey">
                图片ID <span className="text-red-500">*</span>
              </Label>
              <div className="flex space-x-2">
                <Input
                  id="accessKey"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  placeholder="14位唯一标识符"
                  disabled={createImageMutation.isPending}
                  className="flex-1 font-mono"
                  maxLength={14}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateAccessKey}
                  disabled={createImageMutation.isPending}
                  className="px-3"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span className="ml-2">生成ID</span>
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                默认生成随机ID。可手动修改，用于误删图片记录时恢复对应图片访问地址。<br />
                最终访问地址由图片ID与上传图片文件名后缀组成，恢复记录请注意文件名后缀需一致。
              </p>
            </div>
          )}

          {/* 归属工作流名称 */}
          <div className="space-y-2">
            <Label htmlFor="workflowName">
              归属工作流名称 <span className="text-red-500">*</span>
            </Label>
            <div className="flex space-x-2">
              <Input
                id="workflowName"
                ref={workflowInputRef}
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder="输入工作流名称"
                disabled={updateImageMutation.isPending || createImageMutation.isPending}
                className="flex-1"
              />
              {workflows.length > 0 && (
                <Select onValueChange={setWorkflowName}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="选择历史" />
                  </SelectTrigger>
                  <SelectContent>
                    {workflows.map((workflow) => (
                      <SelectItem key={workflow} value={workflow}>
                        {workflow}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* 图片上传/显示 */}
          <div className="space-y-2">
            <ImageUpload
              label={isEditing ? "图片" : "图片上传"}
              required={!isEditing}
              value={editingImage?.accessUrl ? `${editingImage.accessUrl}?t=${new Date(editingImage.updatedAt).getTime()}` : undefined}
              previewUrl={previewUrl}
              onChange={handleFileChange}
              onError={setError}
              disabled={updateImageMutation.isPending || createImageMutation.isPending}
              placeholder={isEditing ? "点击重新上传图片（保持访问链接不变）" : "点击或拖拽图片到此处上传"}
              originalFilename={editingImage?.originalName}
            />
            {isEditing && selectedFile && (
              <p className="text-xs text-amber-600">
                 图片更新，原访问链接保持不变
              </p>
            )}
          </div>

          {/* 错误信息 */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateImageMutation.isPending || createImageMutation.isPending}
            >
              取消
            </Button>
            <Button
              type="submit"
              disabled={
                !workflowName.trim() || 
                (!isEditing && !accessKey.trim()) ||
                (updateImageMutation.isPending || createImageMutation.isPending) ||
                (!isEditing && !selectedFile)
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {updateImageMutation.isPending ? '更新中...' : 
               createImageMutation.isPending ? '上传中...' : 
               isEditing ? '更新' : '上传'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
